// ./src/components/features/Forms/FormFieldsEditModal/UrlFieldOptionsEditor.tsx
import React from "react";
import { Stack, Switch, Select, Text } from "@mantine/core";
import { UrlFieldOptions } from "./fieldOptionsTypes";

export interface UrlFieldOptionsEditorProps {
  value: UrlFieldOptions;
  onChange: (options: UrlFieldOptions) => void;
}

const URL_FORMAT_OPTIONS = [
  { value: "any", label: "Any valid URL (default)" },
  { value: "domain_only", label: "Domain only (no path)" },
  { value: "with_path", label: "Must include path" },
];

export const UrlFieldOptionsEditor: React.FC<UrlFieldOptionsEditorProps> = ({
  value,
  onChange,
}) => {
  const handleChange = (field: keyof UrlFieldOptions, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Configure URL validation options.
      </Text>

      {/* Domain Validation */}
      <Switch
        label="Verify domain exists"
        description="Check that the URL domain has valid DNS records"
        checked={value.validateDomainExists ?? false}
        onChange={(e) =>
          handleChange("validateDomainExists", e.currentTarget.checked)
        }
        size="sm"
      />

      <Switch
        label="Verify URL is live"
        description="Check that the URL responds to requests (HEAD request)"
        checked={value.validateUrlIsLive ?? false}
        onChange={(e) =>
          handleChange("validateUrlIsLive", e.currentTarget.checked)
        }
        size="sm"
      />

      {/* URL Format */}
      <Select
        label="URL format"
        description="Control what URL structure is accepted"
        data={URL_FORMAT_OPTIONS}
        value={value.urlFormat ?? "any"}
        onChange={(val) =>
          handleChange("urlFormat", val === "any" ? undefined : val)
        }
        size="sm"
      />

      {/* Query and Hash control */}
      <Switch
        label="Allow query parameters"
        description="Accept URLs with ?key=value parameters"
        checked={value.allowQuery ?? true}
        onChange={(e) =>
          handleChange(
            "allowQuery",
            e.currentTarget.checked ? undefined : false
          )
        }
        size="sm"
      />

      <Switch
        label="Allow hash/fragment"
        description="Accept URLs with #section fragments"
        checked={value.allowHash ?? true}
        onChange={(e) =>
          handleChange("allowHash", e.currentTarget.checked ? undefined : false)
        }
        size="sm"
      />
    </Stack>
  );
};
